import React, { useState, useEffect, useRef } from 'react';
import './StudentDashboard.css';
import AdditionalInfo from './AdditionalInfo';
import ELogbook from './eLogbook';
import Results from './Results';
import PasswordChangeModal from './PasswordChangeModal';
import collegeConfig from '../../config/collegeConfig';
import ManageAccountModal from './ManageAccountModal';
import { subscribeToPushNotifications, checkSubscriptionStatus } from '../../utils/pushNotificationHelper';
import { Capacitor } from '@capacitor/core';
import NotificationPrompt from './NotificationPrompt';
import {
  FaBookMedical,
  FaChartBar,
  FaInfoCircle,
  FaSignOutAlt,
  FaBell,
  FaTimes,
  FaUser,
  FaHospital,
  FaCalendarAlt,
  FaAward,
  FaTrophy,
  FaNewspaper,
  FaIdCard,
  FaGraduationCap,
  FaClipboardCheck,
  FaExclamationTriangle,
} from 'react-icons/fa';
import DRPModule from './DRPModule';
import ClassicDRPModule from './ClassicDRPModule';
import SubscriptionRenewal from './SubscriptionRenewal';
import LeaveRequest from './LeaveRequest';
import StudentCertificates from './StudentCertificates';
import StudentAchievements from './StudentAchievements';
import StudentDissertation from './StudentDissertation';
import ExamEligibility from './ExamEligibility';
import AppDownloadBanner from '../AppDownloadBanner/AppDownloadBanner';
import DemoVideoPlayer from '../DemoVideoPlayer/DemoVideoPlayer';

const StudentDashboard = ({ onNavigate }) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);
  const [currentView, setCurrentView] = useState('dashboard');
  const [drpMode, setDrpMode] = useState('advanced');
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [enrolledCourse, setEnrolledCourse] = useState(null);
  const [showManageAccount, setShowManageAccount] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    submittedLogbooks: 0,
    verifiedLogbooks: 0
  });
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [hodReattemptCount, setHodReattemptCount] = useState(0);
  const [hodReattemptRequesters, setHodReattemptRequesters] = useState([]);
  const [elogbookInitialFilter, setElogbookInitialFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(Date.now());
  const [weeklyLogbookStatus, setWeeklyLogbookStatus] = useState({
    hasSubmittedThisWeek: false,
    lastSubmissionDate: null,
    loading: true
  });

    const refreshDashboardData = async () => {
    if (!user) return;
    
    console.log('📊 Refreshing dashboard data...');
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const userId = user.id || user._id;
      
      // Fetch student's enrolled course
      const studentResponse = await fetch(`${collegeConfig.api.baseURL}/students/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (studentResponse.ok) {
        const data = await studentResponse.json();
        if (data.student?.course) {
          setEnrolledCourse(data.student.course);
        }
      }
      
      // Fetch logbook statistics
      const statsResponse = await fetch(`${collegeConfig.api.baseURL}/logbook/stats/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        const stats = statsData.stats;
        
        const submitted = stats.submitted + stats.underReview + stats.approved + stats.rejected + stats.needsRevision;
        const verified = stats.approved;

        setDashboardStats({
          submittedLogbooks: submitted,
          verifiedLogbooks: verified
        });
        setFlaggedCount(stats.repeatRemedialCount || 0);
        setHodReattemptCount(stats.hodReattemptCount || 0);
        setHodReattemptRequesters(stats.hodReattemptRequesters || []);
      }

      // Refresh weekly logbook status
      await checkWeeklyLogbookStatus();
      
      // Refresh notifications
      const notificationsResponse = await fetch(`${collegeConfig.api.baseURL}/notices/dashboard/students`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (notificationsResponse.ok) {
        const notifData = await notificationsResponse.json();
        setNotifications(notifData.notices || []);
        
        const unread = notifData.notices.filter(notice => {
          if (!notice.readBy || notice.readBy.length === 0) return true;
          return !notice.readBy.some(read => 
            (read.userId === userId || read.userId?._id === userId || read.userId?.toString() === userId.toString())
          );
        }).length;
        setUnreadNotifications(unread);
      }
      
      setLastRefreshTime(Date.now());
      
    } catch (error) {
      console.error('Error refreshing dashboard:', error);
      setError('Failed to refresh dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Close profile menu when clicking outside
  useEffect(() => {
    if (!showProfileMenu) return;
    const handleOutsideClick = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [showProfileMenu]);

  // Handle browser back button - only for dashboard level navigation
  useEffect(() => {
    const handlePopState = (event) => {
      // Only handle back button if we're at dashboard level (not in eLogbook's internal navigation)
      if (event.state && event.state.page === 'dashboard') {
        if (event.state.view && event.state.view !== currentView) {
          setCurrentView(event.state.view);
        } else if (currentView !== 'dashboard') {
          setCurrentView('dashboard');
        } else {
          // If already on dashboard main view and user presses back, reload to logout
          window.location.reload();
        }
      }
    };

    // Only push state when navigating to/from dashboard, not for eLogbook internal navigation
    if (currentView === 'dashboard' && (!window.history.state || window.history.state.view !== 'dashboard')) {
      window.history.pushState({ page: 'dashboard', view: 'dashboard' }, '', window.location.href);
    }
    
    // Listen for back button
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentView]);

  // Update history when changing main views (dashboard level only)
  useEffect(() => {
    if (currentView !== 'dashboard' && currentView !== 'elogbook' && currentView !== 'additional-info' && currentView !== 'results') {
      // Don't push state for non-main views
      return;
    }
    
    if (currentView !== 'dashboard') {
      window.history.pushState({ page: 'dashboard', view: currentView }, '', window.location.href);
    }
  }, [currentView]);

  // Fetch DRP mode
  useEffect(() => {
    fetch(`${collegeConfig.api.baseURL}/system-settings`)
      .then(r => r.json())
      .then(d => { if (d.success) setDrpMode(d.drpMode); })
      .catch(() => {});
  }, []);

  // Get user data from localStorage on mount
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!token || !userData) {
      if (onNavigate) {
        onNavigate('login');
      }
      return;
    }
    
    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.role !== 'student') {
        onNavigate('login');
        return;
      }
      setUser(parsedUser);

      // Check if password change is required
      if (parsedUser.requirePasswordChange) {
        setShowPasswordChange(true);
      }

      // Verify fresh subscription status from server (catches expiry + admin changes)
      fetch(`${collegeConfig.api.baseURL}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(data => {
          if (data.user?.subscription) {
            const freshSub = data.user.subscription;
            // Also check date-based expiry in case server hasn't updated the status yet
            const isDateExpired =
              freshSub.expiryDate && new Date(freshSub.expiryDate) < new Date();
            const resolvedStatus =
              freshSub.status !== 'not_required' && isDateExpired ? 'expired' : freshSub.status;

            const updatedUser = {
              ...parsedUser,
              subscription: { ...freshSub, status: resolvedStatus }
            };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
          }
        })
        .catch(() => {
          // Fallback: check date expiry on the cached user
          if (
            parsedUser.subscription?.status !== 'not_required' &&
            parsedUser.subscription?.expiryDate &&
            new Date(parsedUser.subscription.expiryDate) < new Date()
          ) {
            setUser(prev => ({
              ...prev,
              subscription: { ...prev.subscription, status: 'expired' }
            }));
          }
        });
    } catch (error) {
      console.error('Error parsing user data:', error);
      if (onNavigate) {
        onNavigate('login');
      }
    }
  }, [onNavigate]);

  // Fetch student course and logbook statistics
  useEffect(() => {
    const fetchStudentData = async () => {
      if (!user) return;
      
      try {
        const token = localStorage.getItem('token');
        const userId = user.id || user._id;
        
        // Fetch student's enrolled course
        const studentResponse = await fetch(`${collegeConfig.api.baseURL}/students/${userId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (studentResponse.ok) {
          const data = await studentResponse.json();
          if (data.student?.course) {
            setEnrolledCourse(data.student.course);
          }
        }
        
        // Fetch logbook statistics using the stats endpoint
        const statsResponse = await fetch(`${collegeConfig.api.baseURL}/logbook/stats/${userId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          const stats = statsData.stats;
          
          // Calculate submitted logbooks (all non-draft entries)
          const submitted = stats.submitted + stats.underReview + stats.approved + stats.rejected + stats.needsRevision;
          
          // Verified logbooks are approved entries
          const verified = stats.approved;
          
          setDashboardStats({
            submittedLogbooks: submitted,
            verifiedLogbooks: verified
          });
          setFlaggedCount(stats.repeatRemedialCount || 0);
          setHodReattemptCount(stats.hodReattemptCount || 0);
          setHodReattemptRequesters(stats.hodReattemptRequesters || []);
        }

      } catch (error) {
        console.error('Error fetching student data:', error);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    if (user && !showPasswordChange) {
      fetchStudentData();
    }
  }, [user, showPasswordChange]);

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user || showPasswordChange) return;
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${collegeConfig.api.baseURL}/notices/dashboard/students`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setNotifications(data.notices || []);
          
          // Count unread notifications
          const userId = user.id || user._id;
          const unread = data.notices.filter(notice => {
            if (!notice.readBy || notice.readBy.length === 0) return true;
            return !notice.readBy.some(read => 
              (read.userId === userId || read.userId?._id === userId || read.userId?.toString() === userId.toString())
            );
          }).length;
          setUnreadNotifications(unread);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    if (user && !showPasswordChange) {
      fetchNotifications();
      // Refresh notifications every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user, showPasswordChange]);

  // Subscribe to push notifications on mount (only auto-subscribe if permission already granted)
  useEffect(() => {
    const initPushNotifications = async () => {
      if (!user || showPasswordChange) return;
      // On web, only auto-subscribe if permission is already granted — calling
      // Notification.requestPermission() outside a user gesture is blocked by browsers.
      // Native (Capacitor) doesn't use window.Notification so skip this guard.
      if (!Capacitor.isNativePlatform() && (!('Notification' in window) || Notification.permission !== 'granted')) return;

      try {
        const status = await checkSubscriptionStatus();
        if (!status.subscribed) {
          const result = await subscribeToPushNotifications();
          if (result.success) {
            console.log('✅ Push notifications enabled');
          }
        }
      } catch (error) {
        console.error('Error initializing push notifications:', error);
      }
    };

    if (user && !showPasswordChange) {
      setTimeout(initPushNotifications, 2000);
    }
  }, [user, showPasswordChange]);

  // Check weekly logbook status
  useEffect(() => {
    if (user && !showPasswordChange && !loading) {
      checkWeeklyLogbookStatus();
    }
  }, [user, showPasswordChange, loading]);

  const handlePasswordChanged = () => {
    setShowPasswordChange(false);
    // Reload user data
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
    }
  };

  // Check if student has submitted logbook this week
  const checkWeeklyLogbookStatus = async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Get start and end of current week (Monday to Sunday)
      const now = new Date();
      const dayOfWeek = now.getDay();
      const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      
      const weekStart = new Date(now.setDate(diff));
      weekStart.setHours(0, 0, 0, 0);
      
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      // Use the existing /logbook/entries endpoint
      // This endpoint automatically filters by studentId for students
      const response = await fetch(
        `${collegeConfig.api.baseURL}/logbook/entries`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const entries = data.entries || [];
        
        // Check if there are any submitted entries this week
        const thisWeekEntries = entries.filter(entry => {
          // Use submittedAt from metadata if available, otherwise use createdAt
          const submittedAt = entry.metadata?.submittedAt || entry.createdAt;
          const entryDate = new Date(submittedAt);
          
          // Check if entry is within this week and not a draft
          return entryDate >= weekStart && 
                entryDate <= weekEnd && 
                entry.status !== 'draft';
        });

        // Find most recent submission (any non-draft entry)
        let lastSubmission = null;
        const submittedEntries = entries.filter(e => e.status !== 'draft');
        
        if (submittedEntries.length > 0) {
          // Sort by submission date (most recent first)
          const sortedEntries = submittedEntries.sort((a, b) => {
            const dateA = new Date(a.metadata?.submittedAt || a.createdAt);
            const dateB = new Date(b.metadata?.submittedAt || b.createdAt);
            return dateB - dateA; // Descending order
          });
          
          lastSubmission = new Date(
            sortedEntries[0].metadata?.submittedAt || sortedEntries[0].createdAt
          );
        }

        setWeeklyLogbookStatus({
          hasSubmittedThisWeek: thisWeekEntries.length > 0,
          lastSubmissionDate: lastSubmission,
          loading: false
        });
      } else {
        throw new Error('Failed to fetch entries');
      }
    } catch (error) {
      console.error('Error checking weekly logbook status:', error);
      setWeeklyLogbookStatus({
        hasSubmittedThisWeek: false,
        lastSubmissionDate: null,
        loading: false
      });
    }
  };



  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (onNavigate) {
      onNavigate('login');
    } else {
      window.location.reload();
    }
  };

  const handleInternalNavigate = (view) => {
  if (view === 'dashboard') {
    setCurrentView('dashboard');
    refreshDashboardData();
  } else if (view === 'login') {
    handleLogout();
  } else {
    setCurrentView(view);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      const userId = user.id || user._id;
      
      // Check if already read
      const notification = notifications.find(n => n._id === notificationId);
      const isAlreadyRead = notification?.readBy?.some(read => 
        (read.userId === userId || read.userId?._id === userId || read.userId?.toString() === userId.toString())
      );
      
      if (isAlreadyRead) return;
      
      const response = await fetch(`${collegeConfig.api.baseURL}/notices/${notificationId}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        // Update local state
        setNotifications(prev => 
          prev.map(n => n._id === notificationId 
            ? { 
                ...n, 
                readBy: [...(n.readBy || []), { userId, readAt: new Date() }] 
              }
            : n
          )
        );
        setUnreadNotifications(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    markNotificationAsRead(notification._id);
    setSelectedNotification(notification);
    setShowNotifications(false);
  };

  const closeNotificationPopup = () => {
    setSelectedNotification(null);
  };

  const dashboardItems = [
  {
    id: 'eLogbook',
    title: 'eLogbook',
    icon: <FaBookMedical />,
    color: '#48C9B0',
    description: 'Clinical logbook'
  },
  {
    id: 'drp',  
    title: 'DRP',
    icon: <FaHospital />,  
    color: '#E67E22',
    description: 'District Residency'
  },
  {
    id: 'result',
    title: 'Result',
    icon: <FaChartBar />,
    color: '#52BE80',
    description: 'View results'
  },
  {
    id: 'additional-info',
    title: 'Additional Info',
    icon: <FaInfoCircle />,
    color: '#9B59B6',
    description: 'Department-specific information'
  },
  {
    id: 'leave',
    title: 'Leave',
    icon: <FaCalendarAlt />,
    color: '#3498DB',
    description: 'Leave requests'
  },
  {
    id: 'certificates',
    title: 'Certificates',
    icon: <FaAward />,
    color: '#c62828',
    description: 'My certificates'
  },
  {
    id: 'awards',
    title: 'Awards',
    icon: <FaTrophy />,
    color: '#F59E0B',
    description: 'Achievements & honours'
  },
  {
    id: 'publications',
    title: 'Paper / Poster / Publication',
    icon: <FaNewspaper />,
    color: '#0277BD',
    description: 'Research & publications'
  },
  {
    id: 'dissertation',
    title: 'Thesis / Dissertation',
    icon: <FaGraduationCap />,
    color: '#5C6BC0',
    description: 'Submit & track dissertation'
  },
  {
    id: 'exam-eligibility',
    title: 'Exam Eligibility',
    icon: <FaClipboardCheck />,
    color: '#16a085',
    description: 'Check exam eligibility status'
  },
  {
    id: 'generate-cv',
    title: 'Generate CV',
    icon: <FaIdCard />,
    color: '#7D3C98',
    description: 'AI-powered CV from logbook',
    comingSoon: true
  },
  {
    id: 'logout',
    title: 'Logout',
    icon: <FaSignOutAlt />,
    color: '#E74C3C',
    description: 'Sign out'
  }
  ];

  const handleItemClick = (itemId) => {
  if (itemId === 'logout') {
    handleLogout();
    } else if (itemId === 'eLogbook') {
      setElogbookInitialFilter(null);
      setCurrentView('elogbook');
    } else if (itemId === 'drp') {
      setCurrentView('drp');
    } else if (itemId === 'additional-info') {
      setCurrentView('additional-info');
    } else if (itemId === 'result') {
      setCurrentView('results');
    } else if (itemId === 'leave') {
      setCurrentView('leave');
    } else if (itemId === 'certificates') {
      setCurrentView('certificates');
    } else if (itemId === 'awards') {
      setCurrentView('awards');
    } else if (itemId === 'publications') {
      setCurrentView('publications');
    } else if (itemId === 'dissertation') {
      setCurrentView('dissertation');
    } else if (itemId === 'exam-eligibility') {
      setCurrentView('exam-eligibility');
    } else {
      const itemName = dashboardItems.find(item => item.id === itemId)?.title || itemId;
      alert(`${itemName} - Coming Soon!\n\nThis feature is under development and will be available soon.`);
    }
  };

  if (!user) {
    return null;
  }

  if (showPasswordChange) {
    return <PasswordChangeModal onPasswordChanged={handlePasswordChanged} userEmail={user.email} />;
  }

  const studentName = `${user?.profile?.firstName || ''} ${user?.profile?.lastName || ''}`.trim() || 'Student';

  // Handle different views
  if (currentView === 'elogbook') {
    return <ELogbook onNavigate={handleInternalNavigate} parentUser={user} initialFilter={elogbookInitialFilter} />;
  }

  if (currentView === 'additional-info') {
    return <AdditionalInfo onNavigate={handleInternalNavigate} />;
  }

  if (currentView === 'results') {
    return <Results onNavigate={handleInternalNavigate} />;
  }
    
  if (currentView === 'drp') {
    return drpMode === 'classic'
      ? <ClassicDRPModule onNavigate={handleInternalNavigate} />
      : <DRPModule onNavigate={handleInternalNavigate} />;
  }

  if (currentView === 'leave') {
    return <LeaveRequest onNavigate={handleInternalNavigate} parentUser={user} />;
  }

  if (currentView === 'certificates') {
    return <StudentCertificates onNavigate={handleInternalNavigate} />;
  }

  if (currentView === 'awards') {
    return <StudentAchievements onNavigate={handleInternalNavigate} type="award" />;
  }

  if (currentView === 'publications') {
    return <StudentAchievements onNavigate={handleInternalNavigate} type="publication" />;
  }

  if (currentView === 'dissertation') {
    return <StudentDissertation onNavigate={handleInternalNavigate} />;
  }

  if (currentView === 'exam-eligibility') {
    return <ExamEligibility onNavigate={handleInternalNavigate} />;
  }

  const isNotificationRead = (notification) => {
    const userId = user.id || user._id;
    return notification.readBy?.some(read => 
      (read.userId === userId || read.userId?._id === userId || read.userId?.toString() === userId.toString())
    );
  };
  // Helper function to format time since last submission
  const getTimeSinceSubmission = (lastDate) => {
  const now = new Date();
  const diffTime = Math.abs(now - lastDate);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return 'last week';
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 60) return 'last month';
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  return (
    <div className="dashboard-container">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <div className="logo-container">
            <img 
              src={collegeConfig.logo} 
              alt={`${collegeConfig.shortName} Logo`} 
              className="dashboard-logo" 
            />
            <div className="logo-text">
              <h2>{collegeConfig.collegeName}</h2>
              <p>{collegeConfig.appName}</p>
            </div>
          </div>
        </div>

        <div className="header-right">
          {/* Notifications */}
          <div className="notification-wrapper" style={{ position: 'relative', marginRight: '20px' }}>
            <div 
              className="notification-icon"
              onClick={() => setShowNotifications(!showNotifications)}
              style={{
                cursor: 'pointer',
                fontSize: '20px',
                color: '#666',
                position: 'relative'
              }}
            >
              <FaBell />
              {unreadNotifications > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  background: '#E74C3C',
                  color: 'white',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  {unreadNotifications}
                </span>
              )}
            </div>
            
            {showNotifications && (
              <div className="notifications-dropdown" style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                minWidth: '350px',
                maxWidth: 'calc(100vw - 40px)',
                maxHeight: '400px',
                overflow: 'hidden',
                zIndex: 1000,
                marginTop: '10px'
              }}>
                <div style={{
                  padding: '15px 20px',
                  borderBottom: '1px solid #e9ecef',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h3 style={{ margin: 0, fontSize: '16px' }}>Notifications</h3>
                  {unreadNotifications > 0 && (
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      {unreadNotifications} unread
                    </span>
                  )}
                </div>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {notifications.length > 0 ? (
                    notifications.slice(0, 5).map((notification) => (
                      <div 
                        key={notification._id}
                        style={{
                          padding: '15px 20px',
                          borderBottom: '1px solid #f0f0f0',
                          cursor: 'pointer',
                          background: !isNotificationRead(notification) ? '#f8f9fa' : 'white',
                          transition: 'background 0.2s'
                        }}
                        onClick={() => handleNotificationClick(notification)}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 
                          !isNotificationRead(notification) ? '#f8f9fa' : 'white'
                        }
                      >
                        <h4 style={{ margin: '0 0 5px', fontSize: '14px', fontWeight: '600' }}>
                          {notification.title}
                        </h4>
                        <p style={{ margin: '0 0 5px', fontSize: '13px', color: '#666' }}>
                          {notification.content.substring(0, 100)}...
                        </p>
                        <span style={{ fontSize: '11px', color: '#999' }}>
                          {new Date(notification.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '30px', textAlign: 'center', color: '#999' }}>
                      <p>No notifications</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Profile Section */}
          <div className="profile-section" ref={profileMenuRef} onClick={() => setShowProfileMenu(!showProfileMenu)}>
            <div className="profile-avatar">
              {studentName.split(' ').map(n => n[0]).join('')}
            </div>
            <div className="profile-info">
              <span className="profile-name">{studentName}</span>
              <span className="profile-role">
                {enrolledCourse ? `${enrolledCourse.code} Student` : 'PG Student'}
              </span>
            </div>
            <span className="dropdown-arrow">▼</span>
            
            {showProfileMenu && (
              <div className="profile-dropdown">
                <div className="dropdown-item" onClick={() => {
                  setShowManageAccount(true);
                  setShowProfileMenu(false);
                }}>
                  <span className="dropdown-icon"><FaUser /></span>
                  Manage Account
                </div>
                <div className="dropdown-divider"></div>
                <div className="dropdown-item logout" onClick={handleLogout}>
                  <span className="dropdown-icon"><FaSignOutAlt /></span>
                  Log Out
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Notification Popup Modal */}
      {selectedNotification && (
        <div 
          className="notification-modal-overlay"
          onClick={closeNotificationPopup}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px'
          }}
        >
          <div 
            className="notification-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '30px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              animation: 'modalSlideIn 0.3s ease'
            }}
          >
            <button
              onClick={closeNotificationPopup}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '5px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#E74C3C'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
            >
              <FaTimes />
            </button>

            <div style={{ marginBottom: '15px' }}>
              <span style={{
                display: 'inline-block',
                padding: '4px 12px',
                background: selectedNotification.priority === 'high' ? '#E74C3C' : 
                           selectedNotification.priority === 'medium' ? '#F39C12' : '#3498DB',
                color: 'white',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'uppercase'
              }}>
                {selectedNotification.priority} Priority
              </span>
              <span style={{
                display: 'inline-block',
                marginLeft: '10px',
                padding: '4px 12px',
                background: '#9B59B6',
                color: 'white',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'capitalize'
              }}>
                {selectedNotification.type}
              </span>
            </div>

            <h2 style={{
              margin: '0 0 15px',
              fontSize: '24px',
              color: '#333',
              paddingRight: '40px'
            }}>
              {selectedNotification.title}
            </h2>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
              marginBottom: '20px',
              paddingBottom: '20px',
              borderBottom: '1px solid #e9ecef'
            }}>
              <span style={{ fontSize: '13px', color: '#666' }}>
                Posted: {new Date(selectedNotification.publishDate || selectedNotification.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
              {selectedNotification.expiryDate && (
                <span style={{ fontSize: '13px', color: '#E74C3C' }}>
                  Expires: {new Date(selectedNotification.expiryDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              )}
            </div>

            <div style={{
              fontSize: '15px',
              lineHeight: '1.8',
              color: '#555',
              whiteSpace: 'pre-wrap'
            }}>
              {selectedNotification.content}
            </div>

            {selectedNotification.attachments && selectedNotification.attachments.length > 0 && (
              <div style={{ marginTop: '25px' }}>
                <h4 style={{ margin: '0 0 10px', fontSize: '16px', color: '#333' }}>
                  Attachments
                </h4>
                {selectedNotification.attachments.map((attachment, index) => (
                  <a
                    key={index}
                    href={attachment.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      padding: '10px 15px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      marginBottom: '8px',
                      color: '#3498DB',
                      textDecoration: 'none',
                      fontSize: '14px',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#e9ecef'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#f8f9fa'}
                  >
                    📎 {attachment.fileName}
                  </a>
                ))}
              </div>
            )}

            <div style={{
              marginTop: '25px',
              paddingTop: '20px',
              borderTop: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              
              <button
                onClick={closeNotificationPopup}
                style={{
                  padding: '10px 24px',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#5568d3'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#667eea'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Renewal Banner */}
      {user?.subscription?.status !== 'not_required' &&
       (user?.subscription?.status === 'expired' ||
        (user?.subscription?.expiryDate && new Date(user.subscription.expiryDate) < new Date())) && (
        <SubscriptionRenewal
          user={user}
          onRenewed={(newSubscription) => {
            const updatedUser = { ...user, subscription: newSubscription };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
          }}
          onLogout={() => {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.reload();
          }}
        />
      )}

      {/* Welcome Section */}
      <div className="welcome-section">
        <h1 className="welcome-title">Welcome back, {studentName.split(' ')[0]}!</h1>
        <p className="welcome-subtitle">
          {enrolledCourse
            ? `Enrolled in: ${enrolledCourse.name} (${enrolledCourse.code})`
            : 'Access your learning resources and track your progress'}
        </p>
      </div>
      {loading && currentView === 'dashboard' && (
      <div style={{
        position: 'fixed',
        top: '80px',
        right: '30px',
        background: 'rgba(255, 255, 255, 0.95)',
        padding: '12px 20px',
        borderRadius: '8px',
        boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        zIndex: 1000,
        animation: 'slideInRight 0.3s ease'
      }}>
        <div style={{
          width: '20px',
          height: '20px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #667eea',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <span style={{ color: '#667eea', fontWeight: '600', fontSize: '14px' }}>
          Refreshing...
        </span>
      </div>
    )}

    <style jsx>{`
      @keyframes slideInRight {
        from {
          opacity: 0;
          transform: translateX(50px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
    `}</style>

      {/* Dashboard Title */}
      <div className="dashboard-title">
        <h2>Dashboard</h2>
        <div className="title-underline"></div>
      </div>

      {/* Weekly Logbook Status Alert */}
      {!loading && !weeklyLogbookStatus.loading && (
        <div style={{
          maxWidth: '800px',
          margin: '0 auto 30px',
          padding: '0 30px'
        }}>
          {weeklyLogbookStatus.hasSubmittedThisWeek ? (
            // Success - Logbook filled this week
            <div className="status-alert status-success">
              <div className="status-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2"/>
                  <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="status-content">
                <h4>✅ Great Job! Logbook Updated This Week</h4>
                <p>You've submitted your logbook entries for this week. Keep up the excellent work!</p>
              </div>
            </div>
          ) : (
            // Warning - No logbook this week
            <div className="status-alert status-warning">
              <div className="status-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2"/>
                  <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="status-content">
                <h4>⚠️ Logbook Not Filled This Week</h4>
                <p>
                  {weeklyLogbookStatus.lastSubmissionDate 
                    ? `Last submission was ${getTimeSinceSubmission(weeklyLogbookStatus.lastSubmissionDate)}. Don't forget to fill your weekly logbook!`
                    : 'You haven\'t submitted any logbook entries yet. Start filling your logbook today!'}
                </p>
                <button 
                  onClick={() => setCurrentView('elogbook')}
                  className="fill-logbook-btn"
                >
                  Fill Logbook Now →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Repeat/Remedial Flagged Entries Alert */}
      {!loading && flaggedCount > 0 && (
        <div style={{
          maxWidth: '800px',
          margin: '0 auto 30px',
          padding: '0 30px'
        }}>
          <div
            className="status-alert status-danger"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setElogbookInitialFilter('flagged');
              setCurrentView('elogbook');
            }}
          >
            <div className="status-icon">
              <FaExclamationTriangle />
            </div>
            <div className="status-content">
              <h4>Action Needed: Entries Marked Repeat/Remedial</h4>
              <p>
                You have {flaggedCount} {flaggedCount === 1 ? 'entry' : 'entries'} marked Repeat/Remedial — tap to review.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* HOD Reattempt Request Alert */}
      {!loading && hodReattemptCount > 0 && (
        <div style={{
          maxWidth: '800px',
          margin: '0 auto 30px',
          padding: '0 30px'
        }}>
          <div
            className="status-alert status-danger"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setElogbookInitialFilter('flagged');
              setCurrentView('elogbook');
            }}
          >
            <div className="status-icon">
              <FaExclamationTriangle />
            </div>
            <div className="status-content">
              <h4>Action Needed: Reattempt Requested</h4>
              <p>
                {hodReattemptRequesters.join(', ')} {hodReattemptRequesters.length === 1 ? 'is' : 'are'} asking you to reattempt {hodReattemptCount} {hodReattemptCount === 1 ? 'entry' : 'entries'}.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: 'white' }}>
          <div className="spinner" style={{
            border: '4px solid rgba(255, 255, 255, 0.3)',
            borderTop: '4px solid white',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p>Loading dashboard...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{
          background: 'rgba(231, 76, 60, 0.1)',
          border: '1px solid #E74C3C',
          color: '#E74C3C',
          padding: '15px',
          borderRadius: '8px',
          margin: '20px 30px',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}

      {/* Watch Demo */}
      {collegeConfig.demoVideos?.student && (
        <div style={{ textAlign: 'center', padding: '8px 20px 20px' }}>
          <DemoVideoPlayer src={collegeConfig.demoVideos.student} label="Watch Demo" />
        </div>
      )}

      {/* Dashboard Grid */}
      {!loading && !error && (
        <div className="dashboard-grid">
          {dashboardItems.map((item, index) => (
            <div
              key={item.id}
              className={`dashboard-card ${item.isWide ? 'wide-card' : ''}`}
              onClick={item.comingSoon ? undefined : () => handleItemClick(item.id)}
              style={{
                animationDelay: `${index * 0.1}s`,
                '--card-color': item.color,
                position: 'relative',
                ...(item.comingSoon && { opacity: 0.7, cursor: 'default', pointerEvents: 'none' })
              }}
            >
              {item.comingSoon && (
                <div style={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  background: '#7D3C98',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  padding: '3px 8px',
                  borderRadius: '10px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>
                  Coming Soon
                </div>
              )}
              <div className="card-icon" style={{ background: item.color }}>
                <span>{item.icon}</span>
              </div>
              <h3 className="card-title">{item.title}</h3>
              <p className="card-description">{item.description}</p>
              <div className="card-hover-effect"></div>
            </div>
          ))}
        </div>
      )}

      {/* Quick Stats Section */}
      {!loading && !error && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '25px',
          padding: '30px',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '16px',
            padding: '30px',
            textAlign: 'center',
            boxShadow: '0 5px 20px rgba(0, 0, 0, 0.1)',
            border: '2px solid #3498DB'
          }}>
            <h3 style={{ 
              margin: '0 0 10px', 
              color: '#3498DB', 
              fontSize: '40px',
              fontWeight: '700'
            }}>
              {dashboardStats.submittedLogbooks}
            </h3>
            <p style={{ 
              margin: 0, 
              color: '#666', 
              fontSize: '16px',
              fontWeight: '500'
            }}>
              Logbooks Submitted
            </p>
          </div>
          
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '16px',
            padding: '30px',
            textAlign: 'center',
            boxShadow: '0 5px 20px rgba(0, 0, 0, 0.1)',
            border: '2px solid #2ECC71'
          }}>
            <h3 style={{ 
              margin: '0 0 10px', 
              color: '#2ECC71', 
              fontSize: '40px',
              fontWeight: '700'
            }}>
              {dashboardStats.verifiedLogbooks}
            </h3>
            <p style={{ 
              margin: 0, 
              color: '#666', 
              fontSize: '16px',
              fontWeight: '500'
            }}>
              Logbooks Verified
            </p>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="dashboard-footer">
        <AppDownloadBanner />
        <p>© {collegeConfig.copyrightYear} {collegeConfig.collegeName} | {collegeConfig.appName} v{collegeConfig.version}</p>
      </footer>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @media (max-width: 768px) {
          .notifications-dropdown {
            position: fixed !important;
            top: 70px !important;
            left: 20px !important;
            right: 20px !important;
            min-width: auto !important;
            max-width: none !important;
            width: calc(100vw - 40px) !important;
            max-height: calc(100vh - 100px) !important;
          }
        }

        @media (max-width: 480px) {
          .notifications-dropdown {
            left: 10px !important;
            right: 10px !important;
            width: calc(100vw - 20px) !important;
          }

          .notification-modal-content {
            padding: 20px !important;
            max-height: 90vh !important;
          }
        }
      `}</style>

      {/* Manage Account Modal */}
              {showManageAccount && (
                <ManageAccountModal 
                  onClose={() => setShowManageAccount(false)} 
                  user={user}
                />
              )}

      {!showPasswordChange && <NotificationPrompt />}
    </div>
  );
};

export default StudentDashboard;